/**
 * Reading a render shape off a DOM attribute.
 *
 * `data-vo-search-shape="trigger/surface/layout"` is the FIRST-PARTY pin from
 * decision #5050: VO's own chrome -- the instance preview, the /v5 landing page
 * -- may pin what a slot it owns renders. No customer route to it is opened;
 * a customer's markup carries a mount NAME and the shape stays server-side
 * (decision #5067).
 *
 * It lives in its own module because both bundles need it: the customer
 * bootstrap (standalone.jsx) and the demo bundle (demo-embed.js). Importing it
 * from standalone.jsx would drag the whole customer bootstrap -- and its
 * top-level side effects -- into the demo bundle.
 */

/**
 * Read a first-party `trigger/surface/layout` attribute into the triple the
 * widget's resolver takes.
 *
 * Anything that is not a complete triple returns null, so a malformed attribute
 * falls through to server config rather than pinning a half-specified shape.
 * Values are not validated here -- resolveRenderShape repairs unknown ones.
 *
 * @param {string|null} value - the attribute value
 * @returns {{trigger: string, surface: string, layout: string}|null} the triple
 */
export function parseShapeAttr(value) {
  const parts = String(value || '').split('/').map((p) => p.trim());
  if (parts.length !== 3 || parts.some((p) => !p)) return null;
  return { trigger: parts[0], surface: parts[1], layout: parts[2] };
}

/**
 * Read an overlay scope attribute, defaulting to page.
 *
 * Page scope is where every overlay has always gone and where one stays unless
 * a placement asks otherwise. An unrecognised value degrades to page rather
 * than failing: the value comes from first-party markup, so a bad one is a typo
 * in VO's own template and the right answer is the behaviour that already
 * worked.
 *
 * @param {string|null} value - the attribute value
 * @returns {'page'|'self'} the resolved scope
 */
export function parseOverlayScopeAttr(value) {
  return String(value || '').trim() === 'self' ? 'self' : 'page';
}
